import { useEffect, useRef } from 'react';
import io from 'socket.io-client';

import useSocketStore, {
  SOCKET_LIST,
  useSocketActions,
} from '@/stores/SocketStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';

type SocketOwner = symbol;
type SocketClient = ReturnType<typeof io>;
type ServerMessageHandler = (name: string, data: string) => void | Promise<void>;

type ServerMessageSubscription = {
  consumers: Map<SocketOwner, ServerMessageHandler>;
  handlers: Map<string, (data: string) => void>;
};

type WorkspaceRoomSubscription = {
  connectHandler: () => void;
  currentAlias: string;
  owners: Map<SocketOwner, string>;
};

const serverMessageSubscriptions = new WeakMap<
  SocketClient,
  ServerMessageSubscription
>();
const workspaceRoomSubscriptions = new WeakMap<
  SocketClient,
  WorkspaceRoomSubscription
>();

const subscribeToServerMessages = (
  socket: SocketClient,
  owner: SocketOwner,
  consumer: ServerMessageHandler,
) => {
  let subscription = serverMessageSubscriptions.get(socket);

  if (!subscription) {
    const consumers = new Map<SocketOwner, ServerMessageHandler>();
    const handlers = new Map(
      SOCKET_LIST.map((name) => {
        const handler = (data: string) => {
          const activeConsumer = consumers.values().next().value;
          if (activeConsumer) void activeConsumer(name, data);
        };

        socket.on(name, handler);
        return [name, handler] as const;
      }),
    );

    subscription = { consumers, handlers };
    serverMessageSubscriptions.set(socket, subscription);
  }

  const activeSubscription = subscription;
  activeSubscription.consumers.set(owner, consumer);

  return () => {
    activeSubscription.consumers.delete(owner);
    if (activeSubscription.consumers.size > 0) return;

    activeSubscription.handlers.forEach((handler, name) => {
      socket.off(name, handler);
    });
    serverMessageSubscriptions.delete(socket);
  };
};

const subscribeToWorkspaceRoom = (
  socket: SocketClient,
  owner: SocketOwner,
  workspaceAlias: string,
) => {
  let subscription = workspaceRoomSubscriptions.get(socket);

  if (!subscription) {
    const owners = new Map<SocketOwner, string>([[owner, workspaceAlias]]);
    const newSubscription: WorkspaceRoomSubscription = {
      connectHandler: () => {
        socket.emit('join', newSubscription.currentAlias);
      },
      currentAlias: workspaceAlias,
      owners,
    };
    subscription = newSubscription;

    workspaceRoomSubscriptions.set(socket, newSubscription);
    socket.on('connect', newSubscription.connectHandler);
    if (socket.connected) socket.emit('join', workspaceAlias);
  } else {
    subscription.owners.set(owner, workspaceAlias);

    if (subscription.currentAlias !== workspaceAlias) {
      if (socket.connected) socket.emit('leave', subscription.currentAlias);
      subscription.currentAlias = workspaceAlias;
      if (socket.connected) socket.emit('join', workspaceAlias);
    }
  }

  const activeSubscription = subscription;

  return () => {
    activeSubscription.owners.delete(owner);
    if (activeSubscription.owners.size > 0) return;

    socket.off('connect', activeSubscription.connectHandler);
    if (socket.connected) {
      socket.emit('leave', activeSubscription.currentAlias);
    }
    workspaceRoomSubscriptions.delete(socket);
  };
};

export const useSocketInit = () => {
  const { setSocket } = useSocketStore((state) => ({
    setSocket: state.setSocket,
  }));

  useEffect(() => {
    const socket =
      import.meta.env.VITE_PUBLIC_SOCKET_URL &&
      io(`${import.meta.env.VITE_PUBLIC_SOCKET_URL}`, {
        transports: ['websocket', 'polling'],
      });
    setSocket(socket);
  }, [setSocket]);
};

const useSocket = () => {
  const owner = useRef<SocketOwner>(Symbol('useSocket'));
  const workspaceAlias = useWorkspaceStore(
    (state) => state.currWorkspace?.alias,
  );
  const { socketOnServerMessageHandler } = useSocketActions();
  const socket = useSocketStore((state) => state.socket);

  useEffect(() => {
    if (!socket) return;

    return subscribeToServerMessages(
      socket,
      owner.current,
      socketOnServerMessageHandler,
    );
  }, [socket, socketOnServerMessageHandler]);

  useEffect(() => {
    if (!socket || !workspaceAlias) return;

    return subscribeToWorkspaceRoom(socket, owner.current, workspaceAlias);
  }, [socket, workspaceAlias]);
};

export default useSocket;
